import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '@environments/environment';
import { Client, StompSubscription } from '@stomp/stompjs';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import SockJS from 'sockjs-client';
import { AuthentificationService } from '../authentification.service';

/**
 * Enveloppe des messages poussés par le back (`MessageDto<T>` côté socket-server) :
 * un discriminant `type` (cf. `MessageTypeEnum`) et une charge utile `message`.
 * Les consommateurs switchent sur `type` et typent la charge via le paramètre `T`.
 */
export interface SocketMessage<T = any> {
  type: string;
  message: T;
}

const INITIAL_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;
const HEARTBEAT_INTERVAL = 10000;

export const accountTopic = (accountId: number) =>
  `/topic/account-${accountId}`;

export const nextReconnectDelay = (
  currentDelay: number,
  initialDelay = INITIAL_RECONNECT_DELAY,
  maxDelay = MAX_RECONNECT_DELAY,
) => Math.min(Math.max(currentDelay, initialDelay) * 2, maxDelay);

@Injectable()
export class WebsocketService implements OnDestroy {
  private stompClient: Client;
  private readonly connected = new BehaviorSubject<boolean>(false);
  private readonly events = new Subject<SocketMessage>();
  private topicSubscription: StompSubscription | null = null;
  private subscribedTopic: string | null = null;

  constructor(private authentificationService: AuthentificationService) {
    this.connect();

    combineLatest([
      this.authentificationService.authentication,
      this.connected,
    ]).subscribe(([user, isConnected]) => {
      if (!isConnected || !user?.accountId) return;

      this.subscribeToAccountTopic(accountTopic(user.accountId));
    });
  }

  private connect(): void {
    const token = this.authentificationService.getAccessToken();

    this.stompClient = new Client({
      webSocketFactory: () =>
        new SockJS(`${environment.servers.resources.websocketHttp}`),
      connectHeaders: { Authorization: `Bearer ${token}` },
      reconnectDelay: INITIAL_RECONNECT_DELAY,
      heartbeatIncoming: HEARTBEAT_INTERVAL,
      heartbeatOutgoing: HEARTBEAT_INTERVAL,
    });

    this.stompClient.onConnect = () => {
      this.stompClient.reconnectDelay = INITIAL_RECONNECT_DELAY;
      this.connected.next(true);
    };

    this.stompClient.onWebSocketClose = () => {
      this.topicSubscription = null;
      this.subscribedTopic = null;
      this.stompClient.reconnectDelay = nextReconnectDelay(
        this.stompClient.reconnectDelay,
      );
      this.connected.next(false);
    };

    this.stompClient.onWebSocketError = event =>
      console.error('[STOMP] erreur websocket', event);

    this.stompClient.onStompError = frame =>
      console.error(
        '[STOMP] erreur broker',
        frame.headers?.message,
        frame.body,
      );

    this.stompClient.activate();
  }

  private subscribeToAccountTopic(topic: string): void {
    if (this.subscribedTopic === topic) return;

    this.topicSubscription?.unsubscribe();
    this.subscribedTopic = topic;
    this.topicSubscription = this.stompClient.subscribe(topic, message => {
      this.events.next(JSON.parse(message.body));
    });
  }

  /**
   * Flux des messages du compte connecté, partagé par tous les abonnés (une seule
   * souscription STOMP, cf. `subscribeToAccountTopic`).
   *
   * `T` type la charge utile attendue pour les `type` que le consommateur traite ;
   * il reste à `any` par défaut pour les appelants qui lisent le message tel quel.
   */
  public listen<T = any>(): Observable<SocketMessage<T>> {
    return this.events.asObservable() as Observable<SocketMessage<T>>;
  }

  public isConnected(): Observable<boolean> {
    return this.connected.asObservable();
  }

  public disconnect(): void {
    this.topicSubscription?.unsubscribe();
    this.topicSubscription = null;
    this.subscribedTopic = null;
    this.stompClient?.deactivate();
    this.connected.next(false);
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.events.complete();
  }
}
